import { Maximize2, X } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { DipPeekTarget, DipPeekType } from "~/features/dataImportProjects/types";
import { peekPathFor } from "~/features/dataImportProjects/utils/peekPath";
import { BatchChangeRequestsJobDetailPanel } from "./BatchChangeRequestsJobDetailPanel";
import { BatchEvidencesJobDetailPanel } from "./BatchEvidencesJobDetailPanel";
import { EntityComposerDetailPanel, RelationComposerDetailPanel } from "./ComposerDetailPanel";
import { DataSourceDetailPanel } from "./DataSourceDetailPanel";
import { DipRecordsPeek } from "./DipRecordsPeek";
import { EvidenceDraftsPanel } from "./EvidenceDraftsPanel";
import { EvidenceImporterDetailPanel } from "./EvidenceImporterDetailPanel";
import { EvidenceSetDetailPanel } from "./EvidenceSetDetailPanel";
import { RecordUpdateComposerDetailPanel } from "./RecordUpdateComposerDetailPanel";

interface DipNodePeekProps {
  peek: DipPeekTarget | null;
  dipId: string;
  onClose: () => void;
  /** Replace the current peek with another one (e.g. a composer linked from the change-requests table). */
  onOpenPeek: (target: DipPeekTarget) => void;
}

// Peek types that have a real V2 full-page route. Composers, the evidence importer and
// generated records are peek-only on the DIP overview, so they don't show "Open full page".
const FULL_PAGE_PEEK_TYPES = new Set<DipPeekType>([
  "dataSource",
  "evidenceSet",
  "batchEvidencesJob",
  "batchChangeRequestsJob",
  "evidenceDrafts",
]);

const titleKeys: Record<DipPeekType, string> = {
  dataSource: "feature.dataImportProjects.v2.peek.dataSource",
  evidenceSet: "feature.dataImportProjects.v2.peek.evidenceSet",
  batchEvidencesJob: "feature.dataImportProjects.v2.peek.batchEvidencesJob",
  batchChangeRequestsJob: "feature.dataImportProjects.v2.peek.batchChangeRequestsJob",
  evidenceDrafts: "feature.dataImportProjects.v2.peek.evidenceDrafts",
  records: "feature.dataImportProjects.v2.stage.records",
  entityComposer: "feature.dataImportProjects.v2.peek.entityComposer",
  relationComposer: "feature.dataImportProjects.v2.peek.relationComposer",
  recordUpdateComposer: "feature.dataImportProjects.v2.peek.recordUpdateComposer",
  evidenceImporter: "feature.dataImportProjects.v2.peek.evidenceImporter",
};

function DipNodePeekBody({
  peek,
  dipId,
  onOpenPeek,
}: {
  peek: DipPeekTarget;
  dipId: string;
  onOpenPeek: (target: DipPeekTarget) => void;
}) {
  switch (peek.type) {
    case "dataSource":
      return <DataSourceDetailPanel importProjectId={dipId} dataSourceId={peek.id} mode="peek" />;
    case "evidenceSet":
      return (
        <EvidenceSetDetailPanel
          importProjectId={dipId}
          evidenceSetId={peek.id}
          initialTab={peek.initialTab === "evidences" ? "evidences" : "info"}
          mode="peek"
        />
      );
    case "batchEvidencesJob":
      return <BatchEvidencesJobDetailPanel jobId={peek.id} mode="peek" />;
    case "evidenceDrafts":
      return <EvidenceDraftsPanel batchEvidencesJobId={peek.id} />;
    case "batchChangeRequestsJob":
      return (
        <BatchChangeRequestsJobDetailPanel
          jobId={peek.id}
          mode="peek"
          initialTab={peek.initialTab === "changeRequests" ? "changeRequests" : "info"}
          recordComposerDefinitionId={peek.recordComposerDefinitionId}
          onOpenPeek={onOpenPeek}
        />
      );
    case "records":
      return (
        <DipRecordsPeek
          batchChangeRequestsJobId={peek.batchChangeRequestsJobId}
          recordComposerDefinitionId={peek.recordComposerDefinitionId}
          recordKind={peek.recordKind}
        />
      );
    case "entityComposer":
      return <EntityComposerDetailPanel id={peek.id} />;
    case "relationComposer":
      return <RelationComposerDetailPanel id={peek.id} />;
    case "recordUpdateComposer":
      return <RecordUpdateComposerDetailPanel id={peek.id} />;
    case "evidenceImporter":
      return <EvidenceImporterDetailPanel evidenceImporterId={peek.id} />;
  }
}

export function DipNodePeek({ peek, dipId, onClose, onOpenPeek }: DipNodePeekProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = peek !== null;

  const openFullPage = () => {
    if (!peek) return;
    onClose();
    navigate(peekPathFor(peek.type, peek.id, dipId, peek.initialTab));
  };

  return (
    // Non-modal so MUI dialogs rendered inside the peek (e.g. the BCR job run/dry-run confirms) are not
    // made inert by Radix's focus trap. Keep the peek open when interacting with those MUI portals.
    <Sheet open={open} onOpenChange={(next) => !next && onClose()} modal={false}>
      <SheetContent
        side="right"
        showClose={false}
        className="flex w-[55vw] max-w-[55vw] flex-col p-0"
        // Don't auto-focus the header's close/expand button on open: focusing a tooltip-wrapped
        // button opens its Radix tooltip, which then swallows the first Esc (so it took two to close).
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest(".MuiModal-root, .MuiPopover-root, .MuiPopper-root, .MuiAutocomplete-popper")) {
            event.preventDefault();
          }
        }}
      >
        <SheetHeader className="flex flex-row items-center justify-between gap-2">
          <SheetTitle>{peek ? t(titleKeys[peek.type]) : ""}</SheetTitle>
          <div className="flex items-center gap-1">
            {peek && FULL_PAGE_PEEK_TYPES.has(peek.type) ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={openFullPage}
                    aria-label={t("ui.openInFullPage", { defaultValue: "Open full page" })}
                  >
                    <Maximize2 />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("ui.openInFullPage", { defaultValue: "Open full page" })}</TooltipContent>
              </Tooltip>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label={t("ui.close", { defaultValue: "Close" })}
                >
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.close", { defaultValue: "Close" })}</TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>
        <SheetBody>
          <Suspense fallback={<DetailLoading />}>
            {peek !== null && <DipNodePeekBody peek={peek} dipId={dipId} onOpenPeek={onOpenPeek} />}
          </Suspense>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
